/**
 * Endpoint query hooks (list, count, single, summary).
 */

import { useState, useEffect, useCallback } from 'react'
import { useInvalidationSubscription } from '../use-list-invalidation'
import type {
  CaseEndpoint,
  ListEndpointsQuery,
  CollectionStatusSummary,
} from '../../types/case-container'
import {
  listEndpoints,
  listWithOpenQuestions,
  listPendingCollection,
  countEndpoints,
  getCollectionStatusSummary,
  getEndpoint,
} from '../../services/endpoints'

interface UseEndpointsResult {
  endpoints: CaseEndpoint[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useEndpoints(
  caseId: string | null,
  params?: ListEndpointsQuery
): UseEndpointsResult {
  const [endpoints, setEndpoints] = useState<CaseEndpoint[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetch` on every render, which
  // in turn re-fires the `useEffect([fetch])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const {
    nodeCategory,
    deviceType,
    domain,
    search: endpointSearch,
    isActive,
    collectionStatus,
    criticality,
    importance,
    hasOpenQuestion,
    importSource,
  } = params ?? {}

  const fetch = useCallback(async () => {
    if (!caseId) {
      setEndpoints([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const data = await listEndpoints(caseId, params)
      setEndpoints(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch endpoints')
      setEndpoints([])
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    caseId,
    nodeCategory,
    deviceType,
    domain,
    endpointSearch,
    isActive,
    collectionStatus,
    criticality,
    importance,
    hasOpenQuestion,
    importSource,
  ])

  useEffect(() => {
    void fetch()
  }, [fetch])

  useInvalidationSubscription('case_endpoints', fetch)

  return { endpoints, loading, error, refetch: fetch }
}

export function useEndpointsWithOpenQuestions(caseId: string | null): UseEndpointsResult {
  const [endpoints, setEndpoints] = useState<CaseEndpoint[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setEndpoints([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await listWithOpenQuestions(caseId)
      setEndpoints(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch endpoints with open questions')
      setEndpoints([])
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { endpoints, loading, error, refetch: fetch }
}

export function useEndpointsPendingCollection(caseId: string | null): UseEndpointsResult {
  const [endpoints, setEndpoints] = useState<CaseEndpoint[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setEndpoints([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await listPendingCollection(caseId)
      setEndpoints(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch pending collection endpoints')
      setEndpoints([])
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { endpoints, loading, error, refetch: fetch }
}

interface UseEndpointCountResult {
  count: number
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useEndpointCount(caseId: string | null): UseEndpointCountResult {
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setCount(0)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await countEndpoints(caseId)
      setCount(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch endpoint count')
      setCount(0)
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { count, loading, error, refetch: fetch }
}

interface UseCollectionStatusSummaryResult {
  summary: CollectionStatusSummary[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useCollectionStatusSummary(
  caseId: string | null
): UseCollectionStatusSummaryResult {
  const [summary, setSummary] = useState<CollectionStatusSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setSummary([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getCollectionStatusSummary(caseId)
      setSummary(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch collection status summary')
      setSummary([])
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { summary, loading, error, refetch: fetch }
}

interface UseEndpointResult {
  endpoint: CaseEndpoint | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useEndpoint(caseId: string | null, endpointId: string | null): UseEndpointResult {
  const [endpoint, setEndpoint] = useState<CaseEndpoint | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !endpointId) {
      setEndpoint(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getEndpoint(caseId, endpointId)
      setEndpoint(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch endpoint')
      setEndpoint(null)
    } finally {
      setLoading(false)
    }
  }, [caseId, endpointId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { endpoint, loading, error, refetch: fetch }
}
